import { useState, useMemo } from 'react';
import type { Project } from '../types/project';
import type { ProjectFilters } from '../types/filters';

interface UseProjectFiltersProps {
  projects: Project[];
}

export const useProjectFilters = ({ projects }: UseProjectFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ProjectFilters>({});

  const uniqueCustomers = useMemo(() => {
    const customers = new Set(projects.map(project => project.customer));
    return Array.from(customers);
  }, [projects]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value !== undefined) || searchQuery.trim() !== '';
  }, [filters, searchQuery]);

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Search query filter
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        if (
          !project.name.toLowerCase().includes(query) &&
          !project.number.toLowerCase().includes(query) &&
          !project.customer.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      // Type filter
      if (filters.type && project.type !== filters.type) {
        return false;
      }

      // Status filter
      if (filters.status && project.status !== filters.status) {
        return false;
      }

      // Customer filter
      if (filters.customer && project.customer !== filters.customer) {
        return false;
      }

      return true;
    });
  }, [projects, searchQuery, filters]);

  const resetFilters = () => {
    setSearchQuery('');
    setFilters({});
  };

  return {
    filteredProjects,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    resetFilters,
    uniqueCustomers,
    hasActiveFilters
  };
}; 